import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import PrayerCard from "@/components/PrayerCard";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export default function AnsweredPrayers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/", { replace: true });
  }, [authLoading, user, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prayers", { params: { answered: true } });
      setPrayers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  const handleRestore = async (id) => {
    await api.post(`/prayers/${id}/restore`);
    setPrayers((p) => p.filter((x) => x.id !== id));
    toast.success("Prayer restored to Active");
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/prayers/${pendingDelete.id}`);
      setPrayers((p) => p.filter((x) => x.id !== pendingDelete.id));
      toast.success("Prayer removed");
      setPendingDelete(null);
    } catch (e) {
      toast.error("Couldn't delete — please try again");
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[#9B8BAF]" />
      </div>
    );
  }

  return (
    <div className="grain-bg min-h-screen">
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">A grateful log</div>
          <h1 className="mt-2 text-4xl sm:text-5xl text-[#2C2A2E]" data-testid="answered-title">
            Answered prayers
          </h1>
          <p className="mt-3 text-[#75737A]">
            The quiet miracles, small and large. Restore any to bring them back into your active journal.
          </p>
        </div>

        <section className="mt-10">
          {loading ? (
            <div className="py-24 flex items-center justify-center text-[#75737A]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : prayers.length === 0 ? (
            <div className="py-16 md:py-24 text-center" data-testid="answered-empty">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#EFEBF4] flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-[#9B8BAF]" />
              </div>
              <h3 className="mt-6 text-2xl text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                No answered prayers yet
              </h3>
              <p className="mt-2 text-[#75737A] max-w-sm mx-auto">
                When you mark a prayer as answered, it will be gently kept here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
              {prayers.map((p, i) => (
                <PrayerCard
                  key={p.id}
                  prayer={p}
                  index={i}
                  onRestore={() => handleRestore(p.id)}
                  onDelete={() => setPendingDelete(p)}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        title="Delete this prayer?"
        description={
          pendingDelete
            ? `"${pendingDelete.title}" will be permanently removed. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete prayer"
        cancelLabel="Keep it"
        destructive
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
